import React, { useState } from "react";
import { DateInput } from "@/components/DateInput";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Receipt, PenLine } from "lucide-react";
import { eur, fmtDate, todayLocal } from "./format";

const EMPTY = { date: todayLocal(), category: "Altele", description: "", vendor: "", amount: "", currency: "EUR" };

export function ProjectCostsPanel({ projectId, costs, categories, canEdit, onChanged, legacyDirect = 0 }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  async function add() {
    if (!form.date) return toast.error("Completează data cheltuielii");
    if (!(parseFloat(form.amount) > 0)) return toast.error("Suma trebuie să fie pozitivă");
    setBusy(true);
    try {
      await api.post(`/pnl/projects/${projectId}/costs`, { ...form, amount: parseFloat(form.amount) });
      toast.success("Cheltuială adăugată");
      setForm({ ...EMPTY, date: form.date });
      setOpen(false);
      onChanged?.();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  }

  async function remove(c) {
    if (!window.confirm(`Ștergi cheltuiala „${c.description || c.category}" de ${eur(c.amount_eur, 2)}?`)) return;
    try {
      await api.delete(`/pnl/projects/${projectId}/costs/${c.id}`);
      toast.success("Cheltuială ștearsă");
      onChanged?.();
    } catch (e) { toast.error(apiErr(e)); }
  }

  const total = (costs || []).reduce((s, c) => s + (c.amount_eur || 0), 0) + (legacyDirect || 0);
  const hasRows = (costs || []).length > 0 || legacyDirect > 0;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const inp = "pio-input w-full";

  return (
    <div data-testid="pnl-costs-panel">
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="text-[12px] text-slate-500">
          Deconturile aprobate/plătite cu acest proiect intră automat. Aici adaugi cheltuielile directe care nu trec prin decont (subcontractori, materiale, taxe…).
        </div>
        {canEdit && (
          <button data-testid="pnl-cost-add-toggle" onClick={() => setOpen(!open)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-[12px] font-medium bg-[#3d68b1] text-white hover:bg-[#345a9a]">
            <Plus className="h-3.5 w-3.5" /> Adaugă cheltuială
          </button>
        )}
      </div>

      {open && canEdit && (
        <div className="surface-inset p-4 mb-4 grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="pnl-cost-form">
          <label className="text-[11px] text-slate-500">Data
            <DateInput data-testid="pnl-cost-date" value={form.date} onChange={set("date")} className={inp} /></label>
          <label className="text-[11px] text-slate-500">Categorie
            <select data-testid="pnl-cost-category" value={form.category} onChange={set("category")} className={inp}>
              {(categories || ["Altele"]).map((c) => <option key={c} value={c}>{c}</option>)}
            </select></label>
          <label className="text-[11px] text-slate-500 col-span-2">Descriere
            <input data-testid="pnl-cost-desc" value={form.description} onChange={set("description")} placeholder="ex. Studiu geotehnic" className={inp} /></label>
          <label className="text-[11px] text-slate-500">Furnizor
            <input data-testid="pnl-cost-vendor" value={form.vendor} onChange={set("vendor")} placeholder="opțional" className={inp} /></label>
          <label className="text-[11px] text-slate-500">Sumă
            <div className="flex gap-1">
              <input type="number" step="any" min="0" data-testid="pnl-cost-amount" value={form.amount} onChange={set("amount")} placeholder="0" className={`${inp} font-mono`} />
              <select data-testid="pnl-cost-currency" value={form.currency} onChange={set("currency")} className="pio-input w-[72px]">
                <option>EUR</option><option>RON</option>
              </select>
            </div></label>
          <div className="col-span-2 md:col-span-6 flex justify-end gap-2">
            <button onClick={() => setOpen(false)} className="px-3 py-2 text-[12px] text-slate-500 hover:text-slate-800">Renunță</button>
            <button data-testid="pnl-cost-save" onClick={add} disabled={busy}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-[12px] font-medium bg-[#3d68b1] text-white disabled:opacity-60">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Salvează
            </button>
          </div>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="pio-table pio-table-static">
          <thead>
            <tr><th>Data</th><th>Sursă</th><th>Categorie</th><th>Descriere</th><th>Furnizor</th><th className="text-right">Sumă</th><th className="text-right">EUR</th><th></th></tr>
          </thead>
          <tbody>
            {!hasRows && (
              <tr><td colSpan={8} className="py-8 text-center text-slate-400">Nicio cheltuială directă înregistrată.</td></tr>
            )}
            {legacyDirect > 0 && (
              <tr data-testid="pnl-cost-row-legacy" className="bg-slate-50/60">
                <td className="text-[12px] text-slate-400">—</td>
                <td><span className="pio-badge pio-badge-gray">Sumă fixă</span></td>
                <td className="text-[12px]">Alte cheltuieli directe</td>
                <td className="text-[12px] text-slate-700">Sumă fixă setată în dialogul „Finanțe” al proiectului (fără dată)</td>
                <td className="text-[12px] text-slate-500">—</td>
                <td className="text-right tabular-nums text-[12px] text-slate-500">{legacyDirect.toLocaleString("ro-RO", { maximumFractionDigits: 2 })} EUR</td>
                <td className="text-right tabular-nums text-[12px] font-semibold text-slate-800">{eur(legacyDirect, 2)}</td>
                <td />
              </tr>
            )}
            {(costs || []).map((c) => (
              <tr key={c.id} data-testid={`pnl-cost-row-${c.id}`}>
                <td className="tabular-nums text-[12px]">{fmtDate(c.date)}</td>
                <td>
                  {c.source === "decont"
                    ? <span className="pio-badge pio-badge-blue inline-flex items-center gap-1"><Receipt className="h-3 w-3" /> Decont</span>
                    : <span className="pio-badge pio-badge-gray inline-flex items-center gap-1"><PenLine className="h-3 w-3" /> Manual</span>}
                </td>
                <td className="text-[12px]">{c.category}</td>
                <td className="text-[12px] text-slate-700">{c.description || (c.source === "decont" ? c.report_title : "—")}</td>
                <td className="text-[12px] text-slate-500">{c.vendor || "—"}</td>
                <td className="text-right tabular-nums text-[12px] text-slate-500">{(c.amount || 0).toLocaleString("ro-RO", { maximumFractionDigits: 2 })} {c.currency}</td>
                <td className="text-right tabular-nums text-[12px] font-semibold text-slate-800">{eur(c.amount_eur, 2)}</td>
                <td className="text-right">
                  {c.source === "manual" && canEdit && (
                    <button data-testid={`pnl-cost-delete-${c.id}`} onClick={() => remove(c)} className="text-slate-400 hover:text-red-600" title="Șterge">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
          {hasRows && (
            <tfoot>
              <tr>
                <td colSpan={6} className="text-right text-[12px] font-semibold text-slate-600 py-2">Total cheltuieli directe</td>
                <td className="text-right tabular-nums font-bold text-slate-800 py-2" data-testid="pnl-costs-total">{eur(total, 2)}</td>
                <td />
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
